import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Input,
  Select,
  DatePicker,
  Modal,
  Descriptions,
  Alert,
  Row,
  Col,
} from 'antd'
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  FilterOutlined,
} from '@ant-design/icons'
import { fetchAuditLogs, selectAuditLogs, selectMonitorLoading, selectMonitorError } from '../../store/monitorSlice'
import './AuditLogs.css'

const { RangePicker } = DatePicker
const { Option } = Select

const AuditLogs = () => {
  const dispatch = useDispatch()
  const auditLogs = useSelector(selectAuditLogs)
  const loading = useSelector(selectMonitorLoading)
  const error = useSelector(selectMonitorError)

  const [filters, setFilters] = useState({
    event_type: '',
    user_id: '',
    resource_type: '',
    start_time: '',
    end_time: '',
    page: 1,
    page_size: 20,
  })
  const [selectedLog, setSelectedLog] = useState(null)
  const [detailVisible, setDetailVisible] = useState(false)

  useEffect(() => {
    loadLogs()
  }, [filters.page, filters.page_size])

  const loadLogs = () => {
    const params = {}
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params[key] = filters[key]
      }
    })
    dispatch(fetchAuditLogs(params))
  }

  const handleSearch = () => {
    setFilters({ ...filters, page: 1 })
    loadLogs()
  }

  const handleReset = () => {
    setFilters({
      event_type: '',
      user_id: '',
      resource_type: '',
      start_time: '',
      end_time: '',
      page: 1,
      page_size: 20,
    })
  }

  const handleTableChange = (pagination) => {
    setFilters({
      ...filters,
      page: pagination.current,
      page_size: pagination.pageSize,
    })
  }

  const showDetail = (record) => {
    setSelectedLog(record)
    setDetailVisible(true)
  }

  const getEventTypeColor = (eventType) => {
    const colorMap = {
      create: 'success',
      update: 'processing',
      delete: 'error',
      login: 'cyan',
      logout: 'default',
      access: 'blue',
    }
    return colorMap[eventType] || 'default'
  }

  const getEventTypeText = (eventType) => {
    const textMap = {
      create: '创建',
      update: '更新',
      delete: '删除',
      login: '登录',
      logout: '登出',
      access: '访问',
    }
    return textMap[eventType] || eventType
  }

  const columns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '事件类型',
      dataIndex: 'event_type',
      key: 'event_type',
      width: 100,
      render: (text) => (
        <Tag color={getEventTypeColor(text)}>
          {getEventTypeText(text)}
        </Tag>
      ),
    },
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: '资源类型',
      dataIndex: 'resource_type',
      key: 'resource_type',
      width: 120,
    },
    {
      title: '资源 ID',
      dataIndex: 'resource_id',
      key: 'resource_id',
      width: 100,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'IP 地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 140,
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => showDetail(record)}
        >
          详情
        </Button>
      ),
    },
  ]

  return (
    <div className="audit-logs-container">
      <div className="page-header">
        <h1>审计日志</h1>
      </div>

      {error.auditLogs && (
        <Alert
          message="加载失败"
          description={error.auditLogs}
          type="error"
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Card className="filter-card">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="事件类型"
              value={filters.event_type || undefined}
              onChange={(value) => setFilters({ ...filters, event_type: value })}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="create">创建</Option>
              <Option value="update">更新</Option>
              <Option value="delete">删除</Option>
              <Option value="login">登录</Option>
              <Option value="logout">登出</Option>
              <Option value="access">访问</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="用户 ID"
              value={filters.user_id}
              onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="资源类型"
              value={filters.resource_type}
              onChange={(e) => setFilters({ ...filters, resource_type: e.target.value })}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates, dateStrings) => {
                setFilters({
                  ...filters,
                  start_time: dateStrings[0],
                  end_time: dateStrings[1],
                })
              }}
            />
          </Col>
        </Row>
        <Row style={{ marginTop: 16 }}>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadLogs}
                loading={loading.auditLogs}
              >
                刷新
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <Table
          columns={columns}
          dataSource={auditLogs.logs}
          rowKey="id"
          loading={loading.auditLogs}
          pagination={{
            current: filters.page,
            pageSize: filters.page_size,
            total: auditLogs.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title="审计日志详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            关闭
          </Button>,
        ]}
        width={800}
      >
        {selectedLog && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="事件类型">
              <Tag color={getEventTypeColor(selectedLog.event_type)}>
                {getEventTypeText(selectedLog.event_type)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="用户">
              {selectedLog.username} (ID: {selectedLog.user_id})
            </Descriptions.Item>
            <Descriptions.Item label="资源类型">
              {selectedLog.resource_type}
            </Descriptions.Item>
            <Descriptions.Item label="资源 ID">
              {selectedLog.resource_id}
            </Descriptions.Item>
            <Descriptions.Item label="描述">
              {selectedLog.description}
            </Descriptions.Item>
            <Descriptions.Item label="IP 地址">
              {selectedLog.ip_address}
            </Descriptions.Item>
            <Descriptions.Item label="User Agent">
              {selectedLog.user_agent || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="时间">
              {new Date(selectedLog.created_at).toLocaleString('zh-CN')}
            </Descriptions.Item>
            {selectedLog.old_value && (
              <Descriptions.Item label="变更前">
                <pre className="json-display">
                  {JSON.stringify(JSON.parse(selectedLog.old_value), null, 2)}
                </pre>
              </Descriptions.Item>
            )}
            {selectedLog.new_value && (
              <Descriptions.Item label="变更后">
                <pre className="json-display">
                  {JSON.stringify(JSON.parse(selectedLog.new_value), null, 2)}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  )
}

export default AuditLogs
